/* eslint-disable indent */
'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import AgentCard from './agent-card';
import AgentFilters from './agent-filters';
import { authService } from '../../services/auth.service';
import Loading from '../loading';

const AgentList = () => {
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [language, setLanguage] = useState('');
  const [badgeFilter, setBadgeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await authService.getAgents();

      if (response?.data?.items?.length) {
        const apiData = response.data;

        const filteredReraAgents = apiData.items.filter(
          (agent) => agent.is_rera === true
        );

        setAgents(filteredReraAgents);
        setFilteredAgents(filteredReraAgents);

        // Extract unique locations
        const uniqueLocations = [
          ...new Set(
            apiData.items.map((agent) => agent.location).filter(Boolean)
          ),
        ];
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Get unique specializations for filter options
  const specializations = [
    ...new Set(agents.map((agent) => agent.specialization)),
  ];

  // Get unique languages for filter options
  const allLanguages = agents.reduce((langs, agent) => {
    if (agent.languages) {
      agent.languages.forEach((lang) => langs.add(lang));
    }
    return langs;
  }, new Set());
  const languages = [...allLanguages];

  // Filter agents based on search and filter criteria
  useEffect(() => {
    let results = agents;

    if (searchTerm) {
      results = results.filter((agent) =>
        agent.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (specialization) {
      results = results.filter(
        (agent) => agent.specialization === specialization
      );
    }

    if (language) {
      results = results.filter(
        (agent) => agent.languages && agent.languages.includes(language)
      );
    }

    if (badgeFilter) {
      results = results.filter((agent) => {
        switch (badgeFilter) {
          case 'superagent':
            return agent.is_super_agent === true;
          case 'trubroker':
            return agent.is_trubroker === true;
          case 'both':
            return agent.is_super_agent === true && agent.is_trubroker === true;
          default:
            return true;
        }
      });
    }

    setFilteredAgents(results);
  }, [searchTerm, specialization, language, badgeFilter, agents]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  if (isLoading) return <Loading />;

  return (
    <div className='flex flex-col gap-y-6 mb-10'>
      {/* Hero Section */}
      <section className='relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20'>
        {/* Background with animated gradient */}
        <div className='absolute inset-0 bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-950 z-0'>
          <motion.div
            className='absolute inset-0 opacity-30 dark:opacity-40'
            animate={{
              background: [
                'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 80% 70%, rgba(56, 245, 239, 0.15) 0%, rgba(254, 42, 83, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
              ],
            }}
            transition={{
              duration: 15,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          />
        </div>

        {/* Decorative elements */}
        <div className='absolute inset-0 overflow-hidden z-0'>
          {/* Top left circle */}
          <motion.div
            className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-primarycolor/5 dark:bg-primarycolor/10'
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 20,
              ease: 'easeInOut',
            }}
          />

          {/* Bottom right circle */}
          <motion.div
            className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-secondarycolor/5 dark:bg-secondarycolor/10'
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, -10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 25,
              ease: 'easeInOut',
            }}
          />

          {/* Small floating elements */}
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`floating-${i}`}
              className={`absolute w-${(i % 3) + 2} h-${(i % 3) + 2} rounded-full ${
                i % 3 === 0
                  ? 'bg-primarycolor/20'
                  : i % 3 === 1
                    ? 'bg-secondarycolor/20'
                    : 'bg-[#D4AF37]/20'
              }`}
              style={{
                top: `${10 + i * 8}%`,
                left: `${5 + i * 9}%`,
              }}
              animate={{
                y: [0, -20, 0],
                x: [0, 10, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 5 + i,
                ease: 'easeInOut',
                delay: i * 0.3,
              }}
            />
          ))}
        </div>

        <div className='container mx-auto px-4 z-10 py-20'>
          <div className='max-w-7xl mx-auto'>
            {/* Section title with animated underline */}
            <motion.div
              className='text-center mb-16'
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h1
                className='text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-primarycolor to-secondarycolor bg-clip-text text-transparent'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Our Dynamic Team{' '}
                {/* <span className='text-gray-400 dark:text-gray-500'>
                  (我们的团队)
                </span> */}
              </motion.h1>
              <motion.div
                className='h-1 w-24 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto'
                initial={{ width: 0 }}
                animate={{ width: 120 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
              <motion.p
                className='mt-6 text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                Our diverse team brings decades of combined experience and
                expertise in the real estate industry, ensuring personalized
                service and exceptional results for our clients.
              </motion.p>

              {/* Filters */}
              <AgentFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                specialization={specialization}
                setSpecialization={setSpecialization}
                language={language}
                setLanguage={setLanguage}
                badgeFilter={badgeFilter}
                setBadgeFilter={setBadgeFilter}
                specializations={specializations}
                languages={languages}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {!isLoading && !agents.length && (
        <div className='text-center text-red-500'>Failed to load agents.</div>
      )}

      {/* Agent Cards */}
      <motion.div
        variants={containerVariants}
        initial='hidden'
        animate='visible'
        className='flex flex-wrap justify-center gap-8'
      >
        {filteredAgents?.map((agent) => (
          <AgentCard
            key={agent.id || agent.name}
            agent={agent}
            isLoading={isLoading}
          />
        ))}
      </motion.div>

      {/* Results count */}
      <div className='text-center mb-5'>
        <p className='text-gray-600 dark:text-gray-400'>
          {!filteredAgents?.length
            ? 'No agents found matching your criteria'
            : `Showing ${filteredAgents.length} ${filteredAgents.length === 1 ? 'agent' : 'agents'}`}
        </p>
      </div>

      {/* No results message */}
      {!filteredAgents?.length && !isLoading && (
        <div className='text-center py-12'>
          <h3 className='text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-4'>
            No agents found
          </h3>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSpecialization('');
              setLanguage('');
              setBadgeFilter('');
            }}
            className='px-6 py-2 bg-primarycolor text-white rounded-full hover:bg-primarycolor/90 transition-colors'
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentList;
